import type { PropType } from 'vue'

export interface DataViewItem {
  [key: string]: any
}

export interface DataViewLayoutOptions {
  layout: 'list' | 'grid'
}

export interface DataViewSortEvent {
  sortField: string | ((item: any) => any)
  sortOrder: 1 | -1
}

export interface DataViewPageEvent {
  first: number
  rows: number
  page: number
  pageCount: number
}

export interface DataViewFilterEvent {
  filters: { [key: string]: any }
}

export interface DataViewSelectionEvent {
  originalEvent: Event
  value: any
}

export interface DataViewStateEvent {
  page: number
  first: number
  rows: number
  sortField: string | null
  sortOrder: number
  filters: { [key: string]: any }
  selection: any
}

export const dataViewEmits = {
  'update:modelValue': (value: DataViewItem[]) => true,
  'update:first': (value: number) => true,
  'update:rows': (value: number) => true,
  'update:selection': (value: any) => true,
  'update:sortField': (value: string | ((item: any) => any)) => true,
  'update:sortOrder': (value: number) => true,
  'update:layout': (value: string) => true,
  'update:filters': (value: { [key: string]: any }) => true,
  'page': (event: DataViewPageEvent) => true,
  'sort': (event: DataViewSortEvent) => true,
  'filter': (event: DataViewFilterEvent) => true,
  'select': (event: DataViewSelectionEvent) => true,
  'unselect': (event: DataViewSelectionEvent) => true,
  'selectAll': (event: { originalEvent: Event; value: any[] }) => true,
  'unselectAll': (event: { originalEvent: Event; value: any[] }) => true,
  'stateChange': (event: DataViewStateEvent) => true
}

export const dataViewProps = {
  modelValue: {
    type: Array as PropType<DataViewItem[]>,
    default: () => []
  },
  dataKey: {
    type: String,
    required: true
  },
  layout: {
    type: String as PropType<'list' | 'grid'>,
    default: 'list'
  },
  rows: {
    type: Number,
    default: 0
  },
  first: {
    type: Number,
    default: 0
  },
  totalRecords: {
    type: Number,
    default: 0
  },
  paginator: {
    type: Boolean,
    default: false
  },
  paginatorPosition: {
    type: String as PropType<'top' | 'bottom' | 'both'>,
    default: 'bottom'
  },
  alwaysShowPaginator: {
    type: Boolean,
    default: true
  },
  paginatorTemplate: {
    type: String,
    default: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown'
  },
  pageLinkSize: {
    type: Number,
    default: 5
  },
  rowsPerPageOptions: {
    type: Array as PropType<number[]>,
    default: null
  },
  currentPageReportTemplate: {
    type: String,
    default: '({currentPage} of {totalPages})'
  },
  sortField: {
    type: [String, Function] as PropType<string | ((item: any) => any)>,
    default: null
  },
  sortOrder: {
    type: Number as PropType<1 | -1>,
    default: null
  },
  defaultSortOrder: {
    type: Number as PropType<1 | -1>,
    default: 1
  },
  multiSortMeta: {
    type: Array as PropType<{ field: string; order: 1 | -1 }[]>,
    default: null
  },
  sortMode: {
    type: String as PropType<'single' | 'multiple'>,
    default: 'single'
  },
  removableSort: {
    type: Boolean,
    default: false
  },
  filters: {
    type: Object as PropType<{ [key: string]: any }>,
    default: null
  },
  filterLocale: {
    type: String,
    default: undefined
  },
  filterMatchMode: {
    type: String as PropType<'startsWith' | 'contains' | 'endsWith' | 'equals' | 'notEquals' | 'in' | 'lt' | 'lte' | 'gt' | 'gte'>,
    default: 'contains'
  },
  filterDisplay: {
    type: String as PropType<'menu' | 'row'>,
    default: 'menu'
  },
  lazy: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  loadingIcon: {
    type: String,
    default: 'pi pi-spinner'
  },
  rowHover: {
    type: Boolean,
    default: false
  },
  showGridlines: {
    type: Boolean,
    default: false
  },
  stripedRows: {
    type: Boolean,
    default: false
  },
  size: {
    type: String as PropType<'small' | 'normal' | 'large'>,
    default: 'normal'
  },
  responsiveLayout: {
    type: String as PropType<'stack' | 'scroll'>,
    default: 'stack'
  },
  breakpoint: {
    type: String,
    default: '960px'
  },
  // Selection props
  selection: {
    type: [Object, Array] as PropType<any>,
    default: null
  },
  selectionMode: {
    type: String as PropType<'single' | 'multiple' | null>,
    default: null
  },
  selectAll: {
    type: Boolean,
    default: false
  },
  // State props
  stateStorage: {
    type: String as PropType<'session' | 'local' | 'none'>,
    default: 'none'
  },
  stateKey: {
    type: String,
    default: null
  },
  // PT (Pass Through) props
  pt: {
    type: Object as PropType<{
      root?: object
      header?: object
      content?: object
      footer?: object
      loadingOverlay?: object
      loadingIcon?: object
      noData?: object
      grid?: object
      list?: object
      gridItem?: object
      listItem?: object
    }>,
    default: null
  },
  ptOptions: {
    type: Object as PropType<{
      mergeSections?: boolean
      mergeProps?: boolean
    }>,
    default: null
  }
} as const

export type DataViewProps = {
  modelValue: DataViewItem[]
  dataKey: string
  layout?: 'list' | 'grid'
  rows?: number
  first?: number
  totalRecords?: number
  paginator?: boolean
  paginatorPosition?: 'top' | 'bottom' | 'both'
  alwaysShowPaginator?: boolean
  paginatorTemplate?: string
  pageLinkSize?: number
  rowsPerPageOptions?: number[]
  currentPageReportTemplate?: string
  sortField?: string | ((item: any) => any)
  sortOrder?: 1 | -1
  defaultSortOrder?: 1 | -1
  multiSortMeta?: { field: string; order: 1 | -1 }[]
  sortMode?: 'single' | 'multiple'
  removableSort?: boolean
  filters?: { [key: string]: any }
  filterLocale?: string
  filterMatchMode?: 'startsWith' | 'contains' | 'endsWith' | 'equals' | 'notEquals' | 'in' | 'lt' | 'lte' | 'gt' | 'gte'
  filterDisplay?: 'menu' | 'row'
  lazy?: boolean
  loading?: boolean
  loadingIcon?: string
  rowHover?: boolean
  showGridlines?: boolean
  stripedRows?: boolean
  size?: 'small' | 'normal' | 'large'
  responsiveLayout?: 'stack' | 'scroll'
  breakpoint?: string
  selection?: any
  selectionMode?: 'single' | 'multiple' | null
  selectAll?: boolean
  stateStorage?: 'session' | 'local' | 'none'
  stateKey?: string
  pt?: {
    root?: object
    header?: object
    content?: object
    footer?: object
    loadingOverlay?: object
    loadingIcon?: object
    noData?: object
    grid?: object
    list?: object
    gridItem?: object
    listItem?: object
  }
  ptOptions?: {
    mergeSections?: boolean
    mergeProps?: boolean
  }
}
